import { useState, useRef, useCallback } from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";
import { qualityAuditApi } from "../../api/qualityAuditApi";
import { trackToolAction } from "../../utils/analytics";

type BulkRow = { videoId: string; title?: string; description?: string };
type ResultRow = BulkRow & { verdict?: string; error?: string };

const BulkAnalysisTab = () => {
  const [rows, setRows] = useState<BulkRow[]>([]);
  const [channelId, setChannelId] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ResultRow[]>([]);
  const [runId, setRunId] = useState<string | null>(null);
  const [runs, setRuns] = useState<{ runId: string; dateCreated: string; channelId?: string }[]>([]);
  const toast = useRef<Toast>(null);

  const handleCsvLoad = useCallback((data: string[][]) => {
    const mapped: BulkRow[] = data
      .filter((row) => row.some((c) => c?.trim()))
      .map((row) => ({
        videoId: (row[0] ?? "").trim() || (row[2] ?? "").trim(),
        title: (row[1] ?? row[0] ?? "").trim() || undefined,
        description: (row[2] ?? row[1] ?? "").trim() || undefined,
      }))
      .filter((r) => r.videoId);
    setRows(mapped);
    setResults([]);
    setRunId(null);
    toast.current?.show({
      severity: "info",
      summary: "CSV loaded",
      detail: `${mapped.length} rows`,
    });
  }, []);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      Papa.parse(acceptedFiles[0], {
        complete: (res) => {
          const data = (res.data as string[][]).filter(
            (row) => Array.isArray(row) && row.some((c) => c?.trim())
          );
          handleCsvLoad(data);
        },
        skipEmptyLines: true,
      });
    },
    [handleCsvLoad]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "text/csv": [".csv"], "application/vnd.ms-excel": [".csv"] },
    maxFiles: 1,
  });

  const runAnalysis = async () => {
    if (rows.length === 0) {
      toast.current?.show({ severity: "warn", summary: "Load a CSV first" });
      return;
    }
    trackToolAction("Classifiers", "run_bulk");
    setLoading(true);
    setResults([]);
    setRunId(null);
    try {
      const { runId: id, results: res } = await qualityAuditApi.bulkAnalyze({
        rows,
        channelId: channelId.trim() || undefined,
      });
      setRunId(id);
      setResults((res as ResultRow[]) || []);
      toast.current?.show({ severity: "success", summary: "Analysis complete", detail: `Run ID: ${id}` });
    } catch (e: unknown) {
      const msg = e && typeof e === "object" && "response" in e
        ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
        : (e as Error)?.message;
      toast.current?.show({ severity: "error", summary: "Error", detail: String(msg) });
    } finally {
      setLoading(false);
    }
  };

  const loadRunHistory = async () => {
    try {
      const { runs: list } = await qualityAuditApi.listRuns({ limit: 20 });
      setRuns((list as { runId: string; dateCreated: string; channelId?: string }[]) || []);
    } catch {
      setRuns([]);
    }
  };

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <h3 className="mt-0">Bulk Analysis</h3>
      <p className="text-color-secondary mb-4">
        Upload a CSV with columns: videoId, title (optional), description (optional). Then run analysis and download results.
      </p>
      <div className="flex flex-column gap-3 mb-4">
        <div
          {...getRootProps()}
          className={`p-4 border-2 border-dashed surface-border border-round cursor-pointer ${isDragActive ? "surface-hover" : ""}`}
        >
          <input {...getInputProps()} />
          {isDragActive ? <p>Drop the CSV here...</p> : <p>Drag and drop a CSV here, or click to select.</p>}
        </div>
        <div className="flex align-items-center gap-2">
          <label>Channel ID (optional)</label>
          <input
            type="text"
            className="p-inputtext p-component"
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
            placeholder="UC..."
          />
        </div>
        <div className="flex gap-2">
          <Button
            label="Run Analysis"
            icon="pi pi-play"
            loading={loading}
            disabled={loading || rows.length === 0}
            onClick={runAnalysis}
          />
          {runId && (
            <Button
              label="Download CSV"
              icon="pi pi-download"
              onClick={() => {
                trackToolAction("Classifiers", "download");
                qualityAuditApi.downloadExport(runId);
              }}
              severity="secondary"
            />
          )}
          <Button label="Load run history" icon="pi pi-history" onClick={loadRunHistory} severity="help" />
        </div>
      </div>
      {rows.length > 0 && (
        <p className="text-color-secondary">
          Loaded {rows.length} row(s). Columns: videoId, title, description.
        </p>
      )}
      {results.length > 0 && (
        <DataTable value={results} paginator rows={10} className="mt-4">
          <Column field="videoId" header="Video ID" />
          <Column field="title" header="Title" />
          <Column field="verdict" header="Verdict" />
          <Column field="error" header="Error" />
        </DataTable>
      )}
      {runs.length > 0 && (
        <div className="mt-4">
          <h4>Recent runs</h4>
          <ul>
            {runs.map((r) => (
              <li key={r.runId}>
                {r.dateCreated} {r.channelId && `(${r.channelId})`}{" "}
                <Button
                  label="Download CSV"
                  link
                  size="small"
                  onClick={() => {
                    trackToolAction("Classifiers", "download");
                    qualityAuditApi.downloadExport(r.runId);
                  }}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default BulkAnalysisTab;
